/*
 * Multi-series ECharts wrapper — the render side of the DP Charts page.
 *
 * Design notes:
 * - Owns the ECharts instance for its lifetime; parents pass an array of
 *   series descriptors and the wrapper reduces them into one setOption call.
 * - Y-axes are auto-grouped by the leaves' unit dimension (temperature on one
 *   axis, pressure on another, etc.) so a mixed set doesn't get crushed. Per-
 *   series overrides win over the auto-derived axis.
 * - Threshold bands (warnLow/warnHigh/alarmLow/alarmHigh from the leaf DTO)
 *   render as markArea overlays on the series' assigned axis, tinted amber
 *   for warn and red for alarm. Only drawn if the leaf actually carries them.
 * - Legend clicks toggle series visibility. dataZoom is enabled both as
 *   axis-slider and inside-drag so operators can pan/zoom without leaving the
 *   canvas. The zoom survives new data (finer points for the zoomed part, live
 *   refresh): applyOption keeps it as long as the window keeps its length.
 * - An imperative escape hatch (getPngDataUrl) lets the parent implement PNG
 *   export without reaching into ECharts itself. CSV is not taken from the
 *   chart: the chart may show the shape (M4), which is display-only; the
 *   parent fetches exact interval values for export instead.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'preact/compat';
import * as echarts from 'echarts';
import type { ECharts, EChartsOption } from 'echarts';
import type { LeafDto } from '../../api/types';
import { themeSignal } from '../../theme/theme';
import { smoothForDisplay, type AggregationName } from '../../api/history';
import './MultiSeriesChart.css';

/** One time-series point. Second value can be null when a bucket has no
 *  data — echarts renders that as a gap when connectNulls is off. */
export type ChartPoint = [number, number | null];

/** 'step' is only read from older presets and drawn like 'line', which
 *  already draws CoV data as steps. */
export type SeriesRenderType = 'line' | 'area' | 'bar' | 'scatter' | 'step';
export type SeriesLineStyle = 'solid' | 'dashed' | 'dotted';

/** Per-series render options carried through the presets JSON. Missing
 *  fields fall back to sensible defaults derived from the leaf. */
export interface SeriesOptions {
  renderType: SeriesRenderType;
  colour?: string;
  strokeWidth?: number;
  /** Solid (default), dashed or dotted. ECharts translates this to
   *  `lineStyle.type` — non-line render types silently ignore it. */
  lineStyle?: SeriesLineStyle;
  visible?: boolean;
  smooth?: boolean;
  /** Explicit axis-group override, otherwise derived from the leaf's unit
   *  dimension. Same key across series → shared y-axis. */
  axisGroup?: string;
  /** Draw the leaf's warn/alarm thresholds as markArea overlays. */
  showThresholds?: boolean;
  /** Mode "Berechnet" of DP Charts: what is shown per interval. Missing = the
   *  catalog default of the data point. Ignored by the chart itself. */
  calculation?: AggregationName;
}

export interface SeriesDescriptor {
  leaf: LeafDto;
  points: ChartPoint[];
  options: SeriesOptions;
  /** Unit of the points when it differs from the leaf's (e.g. kWh for the
   *  integral over kW). Undefined = the leaf's unit. */
  unit?: string | null;
  /** Legend name; default the leaf's display name. */
  label?: string;
  /** The points are CoV history: each value holds until the next point.
   *  Line and area series are then drawn as steps — a sloped line would show
   *  values that never existed. With `options.smooth` they are drawn as a
   *  smoothed curve instead, as a display option (see smoothForDisplay). */
  stepped?: boolean;
}

export interface MultiSeriesChartHandle {
  /** Snapshot the current canvas as a PNG data URL. */
  getPngDataUrl(): string | null;
  /** Enter (or clear) lineX brush mode. Parents call this when the user
   *  clicks "Annotation erstellen" so the next drag defines a range. */
  enterBrushMode(): void;
  clearBrush(): void;
}

/**
 * One annotation overlay. Instants render as a vertical markLine at `t`;
 * ranges render as a markArea band [start, end]. Colour is subtle amber
 * so operators can see them without them fighting for attention with
 * the series data itself.
 */
export interface AnnotationOverlay {
  id: string;
  kind: 'Instant' | 'Range';
  atMs: number;
  untilMs?: number | null;
  /** Tooltip text — author + preview. */
  label: string;
}

interface Props {
  series: SeriesDescriptor[];
  /** Annotations to draw on the axis. Missing / empty = none. */
  annotations?: AnnotationOverlay[];
  /** Fired when the user brush-selects a time range on the chart. Delivers
   *  [start, end] in ms since epoch. Enables the ECharts brush toolbox. */
  onBrushRange?: (rangeMs: [number, number]) => void;
  /** Fired when the user clicks an existing annotation marker. */
  onAnnotationClick?: (annotationId: string) => void;
  /** Query window [from, to] in ms. Pins the time axis to it, so an empty
   *  stretch at either end stays visible and the zoom refers to the window. */
  window?: [number, number];
  /** Fired when the zoom changes: the visible part of the window in ms, or
   *  `null` when the whole window is visible again. Also fired after new data
   *  or a new window, with the zoom that was kept. */
  onZoom?: (visible: [number, number] | null) => void;
}

/** Palette rotated across series when no per-series colour override is set.
 *  Deliberately colour-vision-safe (nine hues, spaced ~40° in HSL). */
const PALETTE = [
  '#77A22B', '#4FA3E1', '#E8A93D', '#B47AE0', '#5DBE9A',
  '#E24A5F', '#F27B3B', '#7A8DE0', '#63C7D9',
];

/** Colour of the series at `index`: the override, otherwise the palette. The
 *  series editor uses the same function so its swatches match the chart. */
export function seriesColour(options: SeriesOptions, index: number): string {
  return options.colour || PALETTE[index % PALETTE.length];
}

/** Horizontal room per additional y-axis on the same side, in px. */
const AXIS_OFFSET_PX = 64;

/**
 * Derive an axis-group key from a leaf's unit or dimension. The heuristic is
 * blunt on purpose: everything with a temperature unit stacks, everything
 * with a pressure unit stacks, ... — anything unrecognised gets a per-unit
 * bucket so charts still don't crush unless the operator explicitly groups.
 */
function deriveAxisGroup(s: SeriesDescriptor): string {
  const leaf = s.leaf;
  // A derived unit (kWh from kW) is a different quantity than the leaf's.
  if (s.unit !== undefined && s.unit !== leaf.unit) return `unit:${(s.unit ?? '').toLowerCase()}`;
  if (leaf.dimension) return leaf.dimension.toLowerCase();
  const u = (leaf.unit ?? '').trim();
  if (!u) return 'unknown';
  return u.toLowerCase();
}

function readTokens() {
  const s = typeof window === 'undefined' ? null : getComputedStyle(document.documentElement);
  const get = (name: string, fallback: string) => (s?.getPropertyValue(name).trim() || fallback);
  return {
    surface: get('--surface-base', '#16191D'),
    surfacePanel: get('--surface-panel', '#1D2126'),
    text: get('--text-primary', '#E2E5EA'),
    textMuted: get('--text-secondary', '#8A929E'),
    border: get('--border-subtle', '#2E343B'),
    warn: get('--status-warn', '#E8A93D'),
    error: get('--status-error', '#E24A5F'),
  };
}

export const MultiSeriesChart = forwardRef<MultiSeriesChartHandle, Props>((props, ref) => {
  const { series, annotations, onBrushRange, onAnnotationClick, window: timeWindow, onZoom } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  const seriesRef = useRef<SeriesDescriptor[]>(series);
  seriesRef.current = series;
  const annotationsRef = useRef<AnnotationOverlay[] | undefined>(annotations);
  annotationsRef.current = annotations;
  const onBrushRangeRef = useRef<typeof onBrushRange>(onBrushRange);
  onBrushRangeRef.current = onBrushRange;
  const onAnnotationClickRef = useRef<typeof onAnnotationClick>(onAnnotationClick);
  onAnnotationClickRef.current = onAnnotationClick;
  const windowRef = useRef<[number, number] | undefined>(timeWindow);
  windowRef.current = timeWindow;
  const onZoomRef = useRef<typeof onZoom>(onZoom);
  onZoomRef.current = onZoom;

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(containerRef.current);
    const unsubscribeTheme = themeSignal.subscribe(() => applyOption(chart, seriesRef.current, annotationsRef.current, windowRef.current));
    applyOption(chart, seriesRef.current, annotationsRef.current, windowRef.current);

    chart.on('datazoom', () => emitZoom(chart, windowRef.current, onZoomRef.current));

    // Brush toolbox listeners — the parent decides whether to actually use
    // them; the wiring is always live so a re-render with a fresh onBrushRange
    // callback picks up next brush.
    chart.on('brushEnd', (params: unknown) => {
      const cb = onBrushRangeRef.current;
      if (!cb) return;
      // params: { areas: [{ coordRange: [start, end] }] } for cartesian2d.
      const areas = (params as { areas?: Array<{ coordRange?: [number, number] }> }).areas;
      if (!areas || areas.length === 0) return;
      const range = areas[0].coordRange;
      if (!range) return;
      cb([range[0], range[1]]);
      // Clear the brush so the operator doesn't have to before the next one.
      chart.dispatchAction({ type: 'brush', areas: [] });
    });

    chart.on('click', (params: unknown) => {
      const cb = onAnnotationClickRef.current;
      if (!cb) return;
      const p = params as { componentType?: string; markLine?: unknown; markArea?: unknown; name?: string; data?: { annotationId?: string } };
      const annId = p?.data?.annotationId;
      if (typeof annId === 'string') cb(annId);
    });

    return () => {
      unsubscribeTheme();
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    applyOption(chart, series, annotations, timeWindow);
    emitZoom(chart, timeWindow, onZoomRef.current);
  }, [series, annotations, timeWindow?.[0], timeWindow?.[1]]);

  useImperativeHandle(ref, () => ({
    getPngDataUrl: () => {
      const chart = chartRef.current;
      if (!chart) return null;
      // pixelRatio 2 keeps the exported image sharp on 4k monitors; the
      // white background is deliberate — dark-canvas PNGs pasted into
      // reports look broken.
      return chart.getDataURL({ pixelRatio: 2, backgroundColor: '#ffffff' });
    },
    enterBrushMode: () => {
      const chart = chartRef.current;
      if (!chart) return;
      chart.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'brush',
        brushOption: { brushType: 'lineX', brushMode: 'single' },
      });
    },
    clearBrush: () => {
      const chart = chartRef.current;
      if (!chart) return;
      chart.dispatchAction({ type: 'brush', areas: [] });
      chart.dispatchAction({ type: 'takeGlobalCursor' });
    },
  }), []);

  return <div ref={containerRef} class="hv-mseries-chart" />;
});

MultiSeriesChart.displayName = 'MultiSeriesChart';

/** Current zoom of the time axis in percent of the window. */
function currentZoom(chart: ECharts): { start: number; end: number } {
  const dz = (chart.getOption() as { dataZoom?: Array<{ start?: number; end?: number }> } | undefined)?.dataZoom?.[0];
  return { start: dz?.start ?? 0, end: dz?.end ?? 100 };
}

/** Zoom → visible part of the window. dataZoom reports percent of the axis
 *  extent, which is the window because the axis is pinned to it. */
function emitZoom(
  chart: ECharts,
  timeWindow: [number, number] | undefined,
  cb: ((visible: [number, number] | null) => void) | undefined,
): void {
  if (!cb || !timeWindow) return;
  const { start, end } = currentZoom(chart);
  if (start <= 0 && end >= 100) {
    cb(null);
    return;
  }
  const span = timeWindow[1] - timeWindow[0];
  cb([Math.round(timeWindow[0] + (span * start) / 100), Math.round(timeWindow[0] + (span * end) / 100)]);
}

/** Window of the last applyOption per chart, to decide whether the zoom is kept. */
const appliedWindow = new WeakMap<ECharts, [number, number] | undefined>();

/**
 * Zoom to apply with the next option. setOption with notMerge resets it, so
 * it is carried over as long as the window keeps its length (new data, live
 * refresh moving the window with the clock); a different range starts unzoomed.
 */
function keptZoom(chart: ECharts, timeWindow: [number, number] | undefined): { start: number; end: number } {
  const previous = appliedWindow.get(chart);
  appliedWindow.set(chart, timeWindow);
  if (!previous || !timeWindow) return previous === timeWindow ? currentZoom(chart) : { start: 0, end: 100 };
  const span = timeWindow[1] - timeWindow[0];
  const sameLength = Math.abs((previous[1] - previous[0]) - span) < 1000;
  const overlaps = previous[0] < timeWindow[1] && timeWindow[0] < previous[1];
  return sameLength && overlaps ? currentZoom(chart) : { start: 0, end: 100 };
}

/** Step, smoothed (display option) or plain line for a line/area series. */
function applyLineShape(base: Record<string, unknown>, s: SeriesDescriptor): void {
  const smooth = s.options.smooth === true;
  if (!s.stepped) {
    base.smooth = smooth;
    return;
  }
  if (smooth) {
    base.smooth = true;
    base.data = smoothForDisplay(s.points);
  } else {
    base.smooth = false;
    base.step = 'end';
  }
}

function applyOption(
  chart: ECharts,
  series: SeriesDescriptor[],
  annotations?: AnnotationOverlay[],
  timeWindow?: [number, number],
): void {
  const tokens = readTokens();
  const zoom = keptZoom(chart, timeWindow);

  // Group series by their (possibly overridden) axis key. Each group becomes
  // one y-axis; the first group anchors on the left, the next on the right,
  // subsequent groups offset outward. > 4 axis groups is user error — echarts
  // handles it gracefully but the chart gets very cramped.
  const groups = new Map<string, SeriesDescriptor[]>();
  for (const s of series) {
    const key = s.options.axisGroup || deriveAxisGroup(s);
    const bucket = groups.get(key) ?? [];
    bucket.push(s);
    groups.set(key, bucket);
  }
  const groupKeys = Array.from(groups.keys());
  // Eine Gruppe, deren Serien alle ausgeblendet sind, behaelt ihre Achse
  // (die Serien haengen per yAxisIndex daran), zeigt sie aber nicht und
  // belegt keinen Platz links oder rechts. Sonst stand etwa "bar" von vier
  // ausgeblendeten Druecken neben der Drehzahl (23.10.2026).
  const shownKeys = groupKeys.filter((key) => groups.get(key)!.some((s) => s.options.visible !== false));

  const yAxes = groupKeys.map((key) => {
    const shownIdx = shownKeys.indexOf(key);
    const shown = shownIdx >= 0;
    const idx = shown ? shownIdx : 0;
    // Pull the first visible series in the group for its unit label — all
    // series sharing an axis should share a unit dimension by construction.
    const members = groups.get(key)!;
    const first = members.find((s) => s.options.visible !== false) ?? members[0];
    const unit = (first.unit !== undefined ? first.unit : first.leaf.unit) ?? '';
    // The unit stands once as the axis name, not at every tick: with two
    // axes on one side, "25 l/min" next to "45 °C" ran into each other.
    // Axes on one side are AXIS_OFFSET_PX apart; a long name ("l je 5 min")
    // ran into its neighbour. Centred over its axis and truncated to less
    // than that distance it fits; grown outward it was cut off at the edge
    // ("Wh je 5 min" for kWh). An axis without unit stays unnamed instead of
    // showing its group key.
    const left = idx % 2 === 0;
    return {
      type: 'value' as const,
      show: shown,
      name: unit,
      nameTextStyle: {
        color: tokens.textMuted,
        width: AXIS_OFFSET_PX - 8,
        overflow: 'truncate' as const,
        align: 'center' as const,
      },
      nameGap: 12,
      position: left ? 'left' as const : 'right' as const,
      offset: shown ? Math.max(0, Math.floor(idx / 2) * AXIS_OFFSET_PX) : 0,
      scale: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: tokens.text,
        formatter: (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 3 }),
      },
      splitLine: { show: shown && idx === 0, lineStyle: { color: tokens.border, opacity: 0.35 } },
    };
  });

  const legendData: string[] = [];
  const selected: Record<string, boolean> = {};

  const echartsSeries = series.map((s, i) => {
    const key = s.options.axisGroup || deriveAxisGroup(s);
    const yAxisIndex = Math.max(0, groupKeys.indexOf(key));
    const colour = seriesColour(s.options, i);
    const width = s.options.strokeWidth ?? 2;
    const lineType = s.options.lineStyle ?? 'solid';
    const name = s.label ?? (s.leaf.displayName || s.leaf.symbol);
    legendData.push(name);
    selected[name] = s.options.visible !== false;

    const base: Record<string, unknown> = {
      name,
      yAxisIndex,
      showSymbol: false,
      itemStyle: { color: colour },
      lineStyle: { color: colour, width, type: lineType },
      data: s.points,
    };

    switch (s.options.renderType) {
      case 'area':
        base.type = 'line';
        base.areaStyle = { color: colour, opacity: 0.18 };
        applyLineShape(base, s);
        break;
      case 'bar':
        base.type = 'bar';
        base.itemStyle = { color: colour };
        // Line-style is irrelevant for bars — echarts ignores it but leaves
        // the property in the option, which is harmless.
        break;
      case 'scatter':
        base.type = 'scatter';
        base.symbolSize = 6;
        base.showSymbol = true;
        break;
      case 'step':
      case 'line':
      default:
        base.type = 'line';
        applyLineShape(base, s);
        break;
    }

    // Threshold bands: use the leaf's warn/alarm envelope as markArea overlays
    // on this series' axis. Only draw if the operator hasn't opted out AND
    // the leaf actually carries the values (they're optional on the DTO).
    // Thresholds are in the leaf's unit; a derived unit (kWh, count) skips them.
    const sameUnit = s.unit === undefined || s.unit === s.leaf.unit;
    if (s.options.showThresholds !== false && sameUnit) {
      const markAreas: Array<Array<Record<string, unknown>>> = [];
      const leaf = s.leaf;
      if (leaf.alarmLow != null && leaf.alarmLow !== undefined) {
        markAreas.push([
          { yAxis: -Infinity, itemStyle: { color: tokens.error, opacity: 0.10 } },
          { yAxis: leaf.alarmLow },
        ]);
      }
      if (leaf.warnLow != null && leaf.warnLow !== undefined && (leaf.alarmLow == null || leaf.warnLow > leaf.alarmLow)) {
        markAreas.push([
          { yAxis: leaf.alarmLow ?? -Infinity, itemStyle: { color: tokens.warn, opacity: 0.10 } },
          { yAxis: leaf.warnLow },
        ]);
      }
      if (leaf.warnHigh != null && leaf.warnHigh !== undefined) {
        markAreas.push([
          { yAxis: leaf.warnHigh, itemStyle: { color: tokens.warn, opacity: 0.10 } },
          { yAxis: leaf.alarmHigh ?? Infinity },
        ]);
      }
      if (leaf.alarmHigh != null && leaf.alarmHigh !== undefined) {
        markAreas.push([
          { yAxis: leaf.alarmHigh, itemStyle: { color: tokens.error, opacity: 0.10 } },
          { yAxis: Infinity },
        ]);
      }
      if (markAreas.length > 0) {
        base.markArea = { silent: true, data: markAreas };
      }
    }

    return base;
  });

  // Annotation overlay: emitted as a dedicated invisible series so its
  // markLine/markArea live on a single logical "layer" without needing a
  // per-series duplication. amber tint matches the app's subtle warning
  // palette so operators can distinguish "this is a note" from thresholds.
  if (annotations && annotations.length > 0) {
    const markLines: Array<Record<string, unknown>> = [];
    const markAreas: Array<Array<Record<string, unknown>>> = [];
    for (const a of annotations) {
      if (a.kind === 'Instant') {
        markLines.push({
          xAxis: a.atMs,
          name: a.label,
          annotationId: a.id,
          lineStyle: { color: tokens.warn, opacity: 0.55, type: 'dashed', width: 1 },
          label: { show: false },
        });
      } else if (a.kind === 'Range' && a.untilMs != null) {
        markAreas.push([
          { xAxis: a.atMs, itemStyle: { color: tokens.warn, opacity: 0.10 }, annotationId: a.id, name: a.label },
          { xAxis: a.untilMs },
        ]);
      }
    }
    echartsSeries.push({
      type: 'line',
      name: '__annotations',
      // Invisible carrier — the marker overlays are the payload. `data: []`
      // means no line is drawn, but ECharts still needs a series to hang
      // markLine/markArea off.
      data: [],
      showSymbol: false,
      silent: false,
      z: 3,
      markLine: markLines.length > 0
        ? { symbol: 'none', silent: false, data: markLines, label: { show: false } }
        : undefined,
      markArea: markAreas.length > 0
        ? { silent: false, data: markAreas }
        : undefined,
    } as unknown as Record<string, unknown>);
    // Legend would show the carrier — hide it explicitly.
    legendData.push('__annotations');
    selected['__annotations'] = true;
  }

  const option: EChartsOption = {
    animation: false,
    backgroundColor: 'transparent',
    grid: {
      left: 24 + Math.max(0, shownKeys.filter((_, i) => i % 2 === 0).length - 1) * AXIS_OFFSET_PX,
      right: 24 + Math.max(0, shownKeys.filter((_, i) => i % 2 === 1).length - 1) * AXIS_OFFSET_PX,
      // Room for the one-line legend and the axis names below it, and for
      // the time labels and the zoom slider at the bottom.
      top: 56,
      bottom: 60,
      containLabel: true,
    },
    // One scrolling line: a wrapped legend ran over the axis names.
    legend: {
      type: 'scroll',
      data: legendData.filter((n) => n !== '__annotations'),
      selected,
      textStyle: { color: tokens.text, fontSize: 12 },
      pageTextStyle: { color: tokens.textMuted },
      pageIconColor: tokens.text,
      top: 4,
      left: 8,
      right: 8,
      icon: 'roundRect',
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', crossStyle: { color: tokens.border } },
      backgroundColor: tokens.surfacePanel,
      borderColor: tokens.border,
      textStyle: { color: tokens.text, fontSize: 12 },
      valueFormatter: (v) => (typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 3 }) : String(v)),
    },
    xAxis: {
      type: 'time',
      min: timeWindow?.[0],
      max: timeWindow?.[1],
      axisLine: { lineStyle: { color: tokens.border } },
      axisLabel: { color: tokens.text, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: yAxes.length > 0 ? yAxes : [{
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: tokens.text },
      splitLine: { lineStyle: { color: tokens.border, opacity: 0.35 } },
      scale: true,
    }],
    // Both the axis-slider at the bottom AND inside-drag zoom. Users pick
    // whichever is faster for what they're doing.
    dataZoom: [
      { type: 'inside', throttle: 50, start: zoom.start, end: zoom.end },
      {
        type: 'slider',
        start: zoom.start,
        end: zoom.end,
        height: 22,
        bottom: 6,
        borderColor: tokens.border,
        backgroundColor: 'transparent',
        fillerColor: `${tokens.text}22`,
        handleStyle: { color: tokens.text },
        textStyle: { color: tokens.textMuted, fontSize: 10 },
      },
    ],
    series: echartsSeries as unknown as EChartsOption['series'],
    // Toolbox with a lineX brush — the parent enables it by hooking
    // brushEnd (see the wrapper) to open the annotation creator on
    // a completed brush selection. Kept off the visible toolbox so
    // it doesn't add UI chrome; parents that want a "brush mode"
    // button dispatch it themselves.
    toolbox: {
      show: false,
      feature: { brush: { type: ['lineX', 'clear'] } },
    },
    brush: {
      xAxisIndex: 0,
      brushLink: 'all',
      brushMode: 'single',
      brushStyle: { color: `${tokens.warn}33`, borderColor: tokens.warn },
    },
  };

  chart.setOption(option, { notMerge: true, replaceMerge: ['series', 'yAxis'] });
}
